import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import i18next from 'i18next'
import { Box, Stack, Typography, Skeleton, useTheme } from '@mui/material'

import StreepMap from 'components/Cartography'

import { useProject, useProjectProcess } from '../../use-projects'
import { getLatestDeliveredServiceDate } from '../../helpers'
import Table from './Table'
import Process from './Process'

const ItemInfoSkeleton = () => (
  <Stack gap={1}>
    <Skeleton variant={'text'} sx={{ fontSize: 14, width: 120 }} />
    <Skeleton variant={'text'} sx={{ fontSize: 12, width: 200 }} />
  </Stack>
)

const InfoItem = ({ icon, title, content, isLoading = false }) => {
  const theme = useTheme()
  
  if (isLoading) {
    return <ItemInfoSkeleton />
  }

  return (
    <Stack gap={0.5}>
      <Stack direction={'row'} gap={1} alignItems={'center'}>
        <Box sx={{ width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`fa-regular fa-${icon}`} style={{ color: theme.palette.primary.main, fontSize: 14 }} />
        </Box>
        <Typography sx={{ fontSize: 12, fontWeight: 500 }}>{title}</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: theme.palette.grey[700], marginLeft: 3 }}>
        {content || 'Non renseigné'}
      </Typography>
    </Stack>
  )
}

const MapPlaceholder = () => {
  const theme = useTheme()
  const { t } = useTranslation()
  
  return (
    <Box 
      sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        backgroundColor: theme.palette.grey[100],
        borderRadius: 4,
        border: `1px dashed ${theme.palette.grey[300]}`
      }}
    >
      <Stack alignItems={'center'} gap={1}>
        <i className="fa-regular fa-map-location-dot" style={{ fontSize: 24, color: theme.palette.grey[400] }} />
        <Typography sx={{ fontSize: 12, color: theme.palette.grey[500] }}>
          {t('project.map.location_unavailable')}
        </Typography>
      </Stack>
    </Box>
  )
}

const MapSkeleton = () => {
  const theme = useTheme()
  
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <Skeleton 
        variant={'rectangular'} 
        sx={{ 
          width: '100%', 
          height: '100%', 
          borderRadius: 2,
          backgroundColor: theme.palette.grey[200]
        }} 
      />
      
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1
        }}
      >
        <Skeleton 
          variant="circular" 
          width={20} 
          height={20} 
          sx={{ backgroundColor: theme.palette.primary.main }}
        />
      </Box>
    </Box>
  )
}

const Informations = ({ loading, project, fullWidth = false }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const { data: processData } = useProjectProcess()

  const formatDate = (dateString) => {
    if (!dateString) return null
    return new Date(dateString).toLocaleDateString(i18next.language)
  }

  const latestDeliveredServiceDate = useMemo(() => {
    if (!project?.services && !processData) return null
    
    return getLatestDeliveredServiceDate(project?.services || [], processData || []) || project?.mes_at
  }, [project, processData])
  const formattedDeliveredServiceDate = formatDate(latestDeliveredServiceDate)

  const hasValidLocation = project?.site?.location?.latitude && project?.site?.location?.longitude

  const customMapOptions = {
    center: { lat: 48.873259, lng: 2.305976 },
    mapTypeId: 'roadmap',
    disableDefaultUI: true,
    styles: [
      {
        'featureType': 'poi',
        'stylers': [{ 'visibility': 'off' }]
      },
      {
        'featureType': 'poi.park',
        'elementType': 'geometry',
        'stylers': [{ 'visibility': 'on' }]
      },
      {
        'featureType': 'transit',
        'stylers': [{ 'visibility': 'off' }]
      }
    ]
  }
  
  return (
    <Stack
      sx={{
        width: { xs: '100%', md: fullWidth ? '100%' : '80%' },
        minHeight: { xs: 320, sm: 300, md: 220 },
        height: 'auto',
        padding: 3,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`
      }}
    >
      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        gap={3} 
        sx={{ 
          height: '100%',
          minHeight: 'inherit'
        }}
      >
        <Stack sx={{ 
          width: { xs: '100%', md: '35%' },
          minHeight: { xs: 'auto', md: 180 }
        }} gap={3}>
          <Typography 
            component={'span'} 
            sx={{ 
              fontSize: 16,
              fontWeight: 600,
              textAlign: { xs: 'center', md: 'left' }
            }}
          >
            {t('project.informations.title')}
          </Typography>
          
          <Stack gap={3}>
            <InfoItem
              icon="location-dot"
              title={t('project.informations.address')}
              content={project?.site?.full_address}
              isLoading={loading}
            />
            
            {(loading || formattedDeliveredServiceDate) && (
              <InfoItem
                icon="calendar-clock"
                title={t('project.informations.mes')}
                content={formattedDeliveredServiceDate}
                isLoading={loading}
              />
            )}
          </Stack>
        </Stack>

        <Stack sx={{ 
          width: { xs: '100%', md: '65%' }, 
          flex: 1,
          minHeight: { xs: 200 }
        }}>
          {loading ? (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              minHeight: { xs: 200 }
            }}>
              <MapSkeleton />
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              minHeight: { xs: 200 },
              position: 'relative' 
            }}>
              {hasValidLocation ? (
                <Box 
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    minHeight: 'inherit',
                    borderRadius: 4, 
                    overflow: 'hidden',
                    '& > div': {
                      borderRadius: 4,
                      minHeight: 'inherit'
                    }
                  }}
                >
                  <StreepMap
                    hasLegend={false}
                    hasZoom={false}
                    isProjectHomeView={true}
                    disableMarkerPopup={true}
                    showExpandButton={false}
                    showLegend={false}
                    hideLegends={true}
                    mapOptions={customMapOptions}
                    sites={[{
                      name: project.site.name || 'Projet',
                      equipment: {
                        available_level: 'up'
                      },
                      position: {
                        lng: project.site.location.longitude,
                        lat: project.site.location.latitude,
                      }
                    }]}
                  />
                </Box>
              ) : (
                <Box sx={{ 
                  width: '100%', 
                  height: '100%', 
                  minHeight: 'inherit'
                }}>
                  <MapPlaceholder />
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

const CommercialSkeleton = () => (
  <Stack gap={2} alignItems={'center'} sx={{ height: '100%' }}>
    <Skeleton variant={'text'} sx={{ fontSize: 16, width: 140 }} />
    <Stack gap={1} alignItems={'center'} sx={{ justifyContent: 'center', flex: 1 }}>
      <Skeleton variant={'circular'} width={24} height={24} />
      <Skeleton variant={'text'} sx={{ fontSize: 14, width: 100 }} />
      <Skeleton variant={'text'} sx={{ fontSize: 12, width: 120 }} />
      <Skeleton variant={'text'} sx={{ fontSize: 12, width: 200 }} />
    </Stack>
  </Stack>
)

const Commercial = ({ loading, project }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  
  const hide = useMemo(() => {
    return !loading && project?.services?.every(({ state }) => ['production', 'abandoned'].includes(state))
  }, [loading, project])

  if (hide) {
    return null
  }

  return (
    <Stack
      sx={{
        width: { xs: '100%', md: '20%' },
        height: 280,
        padding: 3,
        borderRadius: 4,
        border: `1px solid ${theme.palette.grey[200]}`,
        alignItems: 'center',
        textAlign: 'center'
      }}
    >
      {loading ? (
        <CommercialSkeleton />
      ) : (
        <>
          <Typography 
            component={'span'} 
            sx={{ 
              fontSize: 16,
              fontWeight: 600,
              alignSelf: 'center',
              width: '100%'
            }}
          >
            {t('project.commercial.title')}
          </Typography>
          
          <Stack 
            gap={2} 
            alignItems={'center'} 
            sx={{ 
              justifyContent: 'center',
              flex: 1,
              textAlign: 'center'
            }}
          >
            <Stack alignItems={'center'} gap={1}>
              <Box sx={{ 
                width: 24,
                height: 24, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <i 
                  className="fa-light fa-hammer-brush"
                  style={{ 
                    fontWeight: '400',
                    color: theme.palette.primary.main, 
                    fontSize: 14
                  }} 
                />
              </Box>
              
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {t('project.commercial.cdp')}
              </Typography>
            </Stack>
            
            <Stack gap={1} alignItems={'center'}>
              <Typography sx={{ fontSize: 13, color: theme.palette.grey[700], fontWeight: 500 }}>
                {project?.manager?.name || t('projects.commercial.not_assigned')}
              </Typography>
              <Typography sx={{ fontSize: 12, color: theme.palette.grey[700] }}>
                {project?.manager?.email || t('projects.commercial.email_not_provided')}
              </Typography>
            </Stack>
          </Stack>
        </>
      )}
    </Stack>
  )
}

const Home = () => {
  const { loading, data } = useProject()

  const hideCommercial = useMemo(() => {
    return !loading && data?.services?.every(({ state }) => ['production', 'abandoned'].includes(state))
  }, [loading, data])
  
  return (
    <Stack
      gap={3}
      sx={{
        overflowY: 'scroll',
        height: 'calc(100cqh - 100px)',
        padding: 1
      }}
    >
      <Stack 
        direction={{ xs: 'column', md: 'row' }} 
        gap={3}
        justifyContent={'space-between'}
      >
        <Informations project={data} loading={loading} fullWidth={hideCommercial} />
        {!hideCommercial && <Commercial project={data} loading={loading} />}
      </Stack>
      <Process />
      <Table services={data?.services || []} loading={loading} />
    </Stack>
  )
}

export default Home
